import { Api } from '@/Api';
import { useRoute } from 'vue-router';
import { DEBUG_NAMESPACE, useDebug } from './useDebug';

const { debug } = useDebug(DEBUG_NAMESPACE.TRACKING);

export enum TrackEvents {
  TEST = 'test',
  CLICK = 'click_beacon',
  ITEM_SHARE = 'item_shared',
  SIGN_IN_CLICK = 'sign_in_click',
  SIGN_UP_CLICK = 'sign_up_click',
  PROMPT_SIGN_UP_REACTION = 'auto_sign_up_reactions',
  PROMPT_SIGN_UP_COMMENT = 'auto_sign_up_comment',
  LOGO_CLICK = 'app_logo_click',
  ONBOARDING_STEP_VIEWED = 'onboarding_step_viewed',
  ONBOARDING_USER_INFO = 'onboarding_user_info',
  DOWNLOAD_APP = 'downloaded_app',
  ANNOTATE_SAVED = 'annotations_saved',
  ANNOTATE_VIDEO_SAVED = 'annotations_video_saved',
  ANNOTATE_CANCELED = 'annotations_canceled',
  ANNOTATE_REVERTED = 'annotations_reverted',
  CAMPAIGN_MODAL_VIEWED = 'modal_viewed',
  ITEM_ADD_TO_FAVORITES = 'added_item_favorites',
  ITEM_ADD_TO_COLLECTION = 'added_item_collection',
  MEETING_SHARE_WITH_ATTENDEES = 'meeting.shared_with_attendees',
  MERGE_VIDEO_APPLY = 'merge_videos_applied',
  MERGE_VIDEO_MODAL_OPEN = 'merge_videos_open',
  VIEWER_ITEM_ZOOM_CLICK = 'image_zoom_click',
  VIEWER_ADD_BUTTON = 'viewer_button_modal_viewed',
  VIEWER_TEMPLATE_EDITOR_OPENED = 'viewer_template_editor_opened',
  VIEWER_TEMPLATES_MODAL_VIEWED = 'viewer_templates_modal_viewed',
  VIEWER_TRANSCRIPTION_COPY = 'copy_transcription_clicked',
  VIEWER_TRANSCRIPTION_CORRECTION_APPLY = 'correction_mode_apply',
  VIEWER_TRANSCRIPTION_CORRECTION_CANCEL = 'correction_mode_cancel',
  VIEWER_TRANSCRIPTION_CORRECTION_ENTER = 'correction_mode_entered',
  VIEWER_TRANSCRIPTION_BLOCK_ACCEPT = 'correction_block_accept',
  VIEWER_TRANSCRIPTION_BLOCK_DELETE = 'correction_block_delete',
  VIEWER_TRANSCRIPTION_BLOCK_RESET = 'correction_block_reset',
  VIEWER_TRANSCRIPTION_BLOCK_RESTORE = 'correction_block_restore',
  VIEWER_TRANSCRIPTION_BLOCK_COPY = 'transcription_block_copy',
  VIEWER_TRANSCRIPTION_BLOCK_SHARE = 'transcription_block_share',
  VIEWER_CHAPTER_SHARE = 'chapter_share_link_copied',
  VIEWER_TRANSCRIPTION_REQUEST = 'transcription_requested',
  VIEWER_TRANSCRIPTION_REGENERATE = 'regenerate_clicked',
  VIEWER_TRANSCRIPTION_VIEWED = 'transcription_viewed',
  VIEWER_COPY_LINK_CLICK = 'viewer_copy_link_click',
  VIEWER_SHARE_ITEM_MODAL_OPEN = 'open_share_item_modal',
  VIEWER_SAVE_ITEM_CLICK = 'save_item_click',
  VIEWER_CAMPAIGN_MODAL_VIEWED = 'modal_viewed',
  VIEWER_DOWNLOAD_ITEM_CLICK = 'download_drop_click',
  SMARTACTIONS_CATEGORIZE = 'smartactions_categorize',
  SMARTACTIONS_GENERATE = 'smartactions_generate',
  SMARTACTIONS_SEND_TO = 'smartactions_send_to',
  VIDEOJS_RETRY_PLAYLIST = 'videojs_retry_playlist',
  VIDEOJS_PLAY_CLICK = 'video_play_click',
  VIDEOJS_STALLED = 'videojs_stalled',
  VIDEOJS_WAITING = 'videojs_waiting',
  VIDEOJS_UNPLAYABLE_VIDEO = 'unplayable_video_viewed',
  UPGRADE_CLICK = 'upgrade_clicked',
  CONTACT_US_CLICKED = 'contact_us_clicked',
  VERSIONS_MODAL_OPENED = 'versions_modal_open',
  VERSIONS_MODAL_DUPLICATE_CLICK = 'versions_modal_duplicate',
  VERSIONS_MODAL_RESTORE_CLICK = 'versions_modal_restore',
  INTEGRATIONS_SETUP = 'integrations_setup',
  INTEGRATION_ENABLED = 'integration_enabled',
  INTEGRATION_DISABLED = 'integration_disabled',
  WORKFLOW_ENABLED = 'workflow_enabled',
  WORKFLOW_DISABLED = 'workflow_disabled',
  WORKFLOW_PROPERTY_CHANGED = 'workflow_property_changed',
  SMARTACTIONS_FEEDBACK = 'smartactions_feedback',
  AI_FEEDBACK = 'ai_feedback',
  VIEWER_OCR_TEXT_COPIED = 'ocr_text_copied',
  LIMITS_MODAL_VIEWED = 'limits_modal_viewed',
  LOGS_PANEL_OPENED = 'logs_panel_opened',
  LOGS_NETWORK_ENTRY_CLICKED = 'logs_network_entry_clicked',
  LOGS_PARSE_FAILED = 'logs_parse_failed',
}

// Those events are sending from automatic click beacon tracking in old viewer - needs to be rewritten
export enum TrackClickEvents {
  SHARE_EMAIL_INPUT = 'share_email_inputs',
  SHARE_EMAIL_SUBMIT = 'share_email_submit',
  SHARE_SETTINGS = 'sharesettings-span',
  ITEM_SECURITY_PASSWORD = 'coll-sec-pwd-radio',
  ITEM_SECURITY_EMAIL = 'coll-sec-emails-radio',
  ITEM_SECURITY_ORG_VIEW = 'coll-sec-org-radio',
  ITEM_SECURITY_ORG_EDIT = 'coll-sec-org-edit-radio',
  ITEM_SECURITY_EMAILED_PPL = 'coll-sec-people-radio',
  ITEM_SECURITY_LINK = 'coll-sec-link-radio',
  ITEM_EXPIRATION_AFTER = 'share-security-expiresafter',
  ITEM_EXPIRATION_SPECIFIC_DATE = 'share-security-expiresat',
  ITEM_SHARE_MODAL_BUTTON = 'sharesettings',
  VIEWER_ANALYTICSTAB = 'view-analytics',
  VIEWER_EXPORT_TAB = 'export_integrations',
  ITEM_NAME_EDIT = 'editable-name',
  ITEM_CUSTOMIZE_PAGE = 'footer-actions-customize',
}

export const useTrack = () => {
  const route = useRoute();

  const track = (event: TrackEvents, data: any = {}, asBeacon?: false) => {
    const trackData = {
      params: route?.params,
      route: route?.name,
      design: 'copernicus',
      source_app: 'web',
      ...data,
    };

    debug(`[track] - ${event}`, trackData);
    return Api.track.create(event, trackData, asBeacon);
  };

  const trackClick = (id: TrackClickEvents, data: any = {}, asBeacon?: false) => {
    const trackData = {
      id,
      params: route?.params,
      route: route?.name,
      design: 'copernicus',
      ...data,
    };

    debug(`[trackClick] - ${id}`, trackData);
    return Api.track.create(TrackEvents.CLICK, trackData, asBeacon);
  };

  const identify = (traits: any = {}) => {
    debug(`[identify] - user properties`, traits);
    return Api.track.identify(traits);
  };

  return { track, trackClick, identify };
};
